import { useLayoutEffect, type RefObject } from 'react';

/**
 * Keep an anchored popover inside the window, on both sides.
 *
 * A panel anchored to its trigger with `right: 0` sits at `trigger.right - panel.width`. When the
 * trigger is narrower than the panel is wide — which on a phone is most of them — that number is
 * negative and the panel is drawn off the *left* of the screen. The bulk-edit popover did exactly
 * this: at 375 with one task selected the command bar wrapped its Edit… trigger to x=181..255, the
 * panel is 320 wide, and it spanned −65..255 with its Apply button at −52..17. Fifty-two of Apply's
 * sixty-nine pixels were outside the window; the label was invisible and there was nothing to press.
 *
 * The reason nobody saw it for four releases is worth writing down: **overflow probes cannot detect
 * this.** Content off the left edge does not extend the scrollable area, so `scrollWidth` stays
 * equal to `clientWidth`, no scrollbar appears, and a full-page screenshot looks correct. Five
 * separate review passes reported "no horizontal overflow anywhere" and each was right by the
 * measure it used. The measure that finds it is positional — `getBoundingClientRect()` against
 * `[0, clientWidth]` — which is what `e2e/reachOnAPhone.e2e.spec.ts` asserts.
 *
 * The correction is applied to `right` rather than as a `transform`, deliberately: a transformed
 * element becomes the containing block for any `position: fixed` descendant, and these panels
 * contain pickers that open exactly such a dialog. Nudging the offset the stylesheet already uses
 * changes where the box lands and nothing else.
 */
export function useFitsViewport(
  open: boolean,
  ref: RefObject<HTMLElement | null>,
  /** Breathing room left between the panel and the window edge. */
  margin = 8,
): void {
  useLayoutEffect(() => {
    if (!open) return;
    const el = ref.current;
    if (!el) return;

    const fit = () => {
      // Measure where the stylesheet puts it, not where the last correction left it.
      el.style.removeProperty('right');
      const base = parseFloat(getComputedStyle(el).right);
      const r = el.getBoundingClientRect();
      const vw = document.documentElement.clientWidth;

      /*
        Left wins when the panel is wider than the window has room for. A panel pinned to the right
        edge instead would put its first field off the left, and the first field is the one that
        says what the panel is.
      */
      let shift = 0;
      if (r.left < margin) shift = margin - r.left;
      else if (r.right > vw - margin) shift = Math.max(vw - margin - r.right, margin - r.left);
      if (shift !== 0 && Number.isFinite(base)) el.style.right = `${base - shift}px`;
    };

    fit();
    window.addEventListener('resize', fit);
    return () => {
      window.removeEventListener('resize', fit);
      el.style.removeProperty('right');
    };
  }, [open, ref, margin]);
}
